/**
 * Name filtering utility with regex patterns for German and English
 * Filters out unwanted names based on configurable patterns
 */

// German unwanted name patterns
const GERMAN_UNWANTED_PATTERNS = [
  /^(test|beispiel|demo|muster|vorlage|placeholder|unbekannt|keine|n\/a|n\.a\.|keine angabe)$/i,
  /^(tbd|tbh|wip|wip|todo|fixme|xxx|yyy|zzz)$/i,
  /^(admin|system|root|user|guest|anonymous|default)$/i,
  /^(error|fehler|invalid|ungültig|null|undefined|none)$/i,
  /^(new|neu|old|alt|temp|temporär|backup|copy|kopie)$/i,
];

// English unwanted name patterns
const ENGLISH_UNWANTED_PATTERNS = [
  /^(test|example|demo|sample|template|placeholder|unknown|none|n\/a|n\.a\.|no name)$/i,
  /^(tbd|tbh|wip|todo|fixme|xxx|yyy|zzz)$/i,
  /^(admin|system|root|user|guest|anonymous|default)$/i,
  /^(error|invalid|null|undefined|none)$/i,
  /^(new|old|temp|temporary|backup|copy|duplicate)$/i,
];

// Combined patterns (German + English)
const ALL_UNWANTED_PATTERNS = [...GERMAN_UNWANTED_PATTERNS, ...ENGLISH_UNWANTED_PATTERNS];

/**
 * Check if a name matches any unwanted pattern
 * @param {string} name - The name to check
 * @param {string} language - Language code: 'de' (German), 'en' (English), or 'all' (both)
 * @returns {boolean} - True if name should be filtered out, false if it's valid
 */
export const isUnwantedName = (name, language = 'all') => {
  if (!name || typeof name !== 'string') {
    return true; // Filter out empty or non-string names
  }

  const trimmedName = name.trim();

  if (trimmedName.length === 0) {
    return true; // Filter out empty names
  }

  // Sellers sometimes leave jersey numbers or placeholders in these free-text
  // fields instead of a real name - e.g. "- 8 -", "(10)", "#16", "...". None
  // of those match the denylist below (they're not specific banned words),
  // so require at least 2 actual letters (any language) for a name to count
  // as plausible in the first place.
  const letterCount = (trimmedName.match(/\p{L}/gu) || []).length;
  if (letterCount < 2) {
    return true;
  }

  let patterns = ALL_UNWANTED_PATTERNS;

  if (language === 'de') {
    patterns = GERMAN_UNWANTED_PATTERNS;
  } else if (language === 'en') {
    patterns = ENGLISH_UNWANTED_PATTERNS;
  }

  return patterns.some(pattern => pattern.test(trimmedName));
};

/**
 * Filter an array of names, removing unwanted ones
 * @param {Array} names - Array of name strings
 * @param {string} language - Language code: 'de', 'en', or 'all'
 * @returns {Array} - Filtered array of valid names
 */
export const filterValidNames = (names, language = 'all') => {
  if (!Array.isArray(names)) {
    return [];
  }

  return names.filter(name => !isUnwantedName(name, language));
};

/**
 * Filter an array of objects by name field, removing unwanted names
 * @param {Array} items - Array of objects with name property
 * @param {string} nameField - Property name containing the name (default: 'name')
 * @param {string} language - Language code: 'de', 'en', or 'all'
 * @returns {Array} - Filtered array of objects with valid names
 */
export const filterItemsByName = (items, nameField = 'name', language = 'all') => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(item => {
    const name = item?.[nameField];
    return !isUnwantedName(name, language);
  });
};

/**
 * Filter options array (used in select filters)
 * Options have structure: { option: id, label: name }
 * @param {Array} options - Array of option objects
 * @param {string} language - Language code: 'de', 'en', or 'all'
 * @returns {Array} - Filtered array of valid options
 */
export const filterValidOptions = (options, language = 'all') => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.filter(opt => !isUnwantedName(opt?.label, language));
};

/**
 * Get German unwanted patterns
 * @returns {Array} - Array of regex patterns
 */
export const getGermanPatterns = () => GERMAN_UNWANTED_PATTERNS;

/**
 * Get English unwanted patterns
 * @returns {Array} - Array of regex patterns
 */
export const getEnglishPatterns = () => ENGLISH_UNWANTED_PATTERNS;

/**
 * Add custom unwanted pattern
 * @param {RegExp} pattern - Regex pattern to add
 * @param {string} language - Language code: 'de', 'en', or 'all'
 */
export const addUnwantedPattern = (pattern, language = 'all') => {
  if (!(pattern instanceof RegExp)) {
    throw new Error('Pattern must be a RegExp');
  }

  if (language === 'de') {
    GERMAN_UNWANTED_PATTERNS.push(pattern);
  } else if (language === 'en') {
    ENGLISH_UNWANTED_PATTERNS.push(pattern);
  } else if (language === 'all') {
    GERMAN_UNWANTED_PATTERNS.push(pattern);
    ENGLISH_UNWANTED_PATTERNS.push(pattern);
  }
};

/**
 * Remove unwanted pattern
 * @param {RegExp} pattern - Regex pattern to remove
 * @param {string} language - Language code: 'de', 'en', or 'all'
 */
export const removeUnwantedPattern = (pattern, language = 'all') => {
  if (!(pattern instanceof RegExp)) {
    throw new Error('Pattern must be a RegExp');
  }

  const removeFromArray = (arr, pat) => {
    const index = arr.findIndex(p => p.source === pat.source && p.flags === pat.flags);
    if (index > -1) {
      arr.splice(index, 1);
    }
  };

  if (language === 'de') {
    removeFromArray(GERMAN_UNWANTED_PATTERNS, pattern);
  } else if (language === 'en') {
    removeFromArray(ENGLISH_UNWANTED_PATTERNS, pattern);
  } else if (language === 'all') {
    removeFromArray(GERMAN_UNWANTED_PATTERNS, pattern);
    removeFromArray(ENGLISH_UNWANTED_PATTERNS, pattern);
  }
};

/**
 * Reset patterns to defaults
 * @param {string} language - Language code: 'de', 'en', or 'all'
 */
export const resetPatterns = (language = 'all') => {
  if (language === 'de' || language === 'all') {
    GERMAN_UNWANTED_PATTERNS.length = 0;
    GERMAN_UNWANTED_PATTERNS.push(
      /^(test|beispiel|demo|muster|vorlage|placeholder|unbekannt|keine|n\/a|n\.a\.|keine angabe)$/i,
      /^(tbd|tbh|wip|wip|todo|fixme|xxx|yyy|zzz)$/i,
      /^(admin|system|root|user|guest|anonymous|default)$/i,
      /^(error|fehler|invalid|ungültig|null|undefined|none)$/i,
      /^(new|neu|old|alt|temp|temporär|backup|copy|kopie)$/i
    );
  }

  if (language === 'en' || language === 'all') {
    ENGLISH_UNWANTED_PATTERNS.length = 0;
    ENGLISH_UNWANTED_PATTERNS.push(
      /^(test|example|demo|sample|template|placeholder|unknown|none|n\/a|n\.a\.|no name)$/i,
      /^(tbd|tbh|wip|todo|fixme|xxx|yyy|zzz)$/i,
      /^(admin|system|root|user|guest|anonymous|default)$/i,
      /^(error|invalid|null|undefined|none)$/i,
      /^(new|old|temp|temporary|backup|copy|duplicate)$/i
    );
  }
};

export default {
  isUnwantedName,
  filterValidNames,
  filterItemsByName,
  filterValidOptions,
  getGermanPatterns,
  getEnglishPatterns,
  addUnwantedPattern,
  removeUnwantedPattern,
  resetPatterns,
};
